<template>
	<div class="detail-form">
		<span class="detail-form__info">
			<InformationIcon />
			Digite apenas o CNPJ e status do NDA. Os demais campos serão preenchidos automaticamente.
		</span>
		<div class="row" v-show="!isLoadingInvestors">
			<div class="detail-form__field">
				<label class="detail-form__field--label" for="cnpj">CNPJ: *</label>
				<input
					class="detail-form__field--input"
					:class="[requiredFields.cnpj ? 'alert' : '']"
					id="cnpj"
					type="text"
					placeholder="00.000.000/0000-00"
					v-mask="'##.###.###/####-##'"
					v-model="investor.cnpj"
					@input="searchByCompanyCnpj"
				/>
				<small class="detail-form__field--input--required" v-if="requiredFields.cnpj">{{
					cnpjErrorMessage
				}}</small>
			</div>
			<div class="detail-form__field">
				<label class="detail-form__field--label" for="status">Status do NDA: *</label>
				<GenericMultiselect
					class="detail-form__field--input"
					:customClass="'status'"
					ref="status"
					:options="statusOptions"
					:placeholder="'Status'"
					:fullWidth="!isWeb"
					:searchable="false"
					:clearOnOpen="false"
					:needType="true"
					:type="'status'"
					@changeInput="changeStatusInput"
				/>
			</div>
		</div>
		<div class="row" v-show="!isLoadingInvestors">
			<div class="detail-form__field">
				<label class="detail-form__field--label" for="name">Razão Social:</label>
				<input
					class="detail-form__field--input disabled"
					id="name"
					type="text"
					placeholder="Razão Social do Investidor"
					v-model="investor.socialReason"
				/>
			</div>
			<div class="detail-form__field">
				<label class="detail-form__field--label" for="fantasy_name">Nome Fantasia:</label>
				<input
					class="detail-form__field--input disabled"
					id="fantasy_name"
					type="text"
					placeholder="Nome Fantasia"
					v-model="investor.fantasyName"
				/>
			</div>
		</div>
		<div class="detail-loading-cnpj" v-if="isLoadingCnpj">
			<SpinnerIcon class="detail-loading-cnpj__icon" />
		</div>
		<div
			class="detail-form__actions"
			:class="newInvestor ? 'new-investor' : ''"
			v-show="!isLoadingInvestors"
		>
			<button
				class="detail-form__actions--btn outline"
				@click="handleDeleteModal()"
				v-if="!newInvestor"
			>
				Excluir Investidor
			</button>
			<ButtonPrimary
				label="Salvar"
				:isDisabled="!isCnpjValid"
				size="md"
				@click="validateFields()"
			/>
		</div>
		<div class="detail-loading-screen" v-if="isLoadingInvestors">
			<SpinnerIcon />
		</div>
		<DeleteModal
			:title="'Excluir Investidor'"
			:subtitle="'Tem certeza que deseja excluir este investidor?'"
			:loader="isLoadingInvestors"
			v-show="showDeleteModal"
			@handleDeleteModal="handleDeleteModal()"
			@confirmDelete="confirmDelete()"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
	GET_INVESTOR_BY_ID,
	CREATE_INVESTOR,
	UPDATE_INVESTOR,
	DELETE_INVESTOR,
} from '@/store/modules/investorManagement/actions';
import { mask } from 'vue-the-mask';
import GenericMultiselect from '@/components/shared/GenericMultiselect/GenericMultiselect.vue';
import SpinnerIcon from '@/components/shared/Icons/SpinnerIcon.vue';
import DeleteModal from '@/components/shared/DeleteModal/DeleteModal.vue';
import ButtonPrimary from '@/components/shared/Button/ButtonPrimary/ButtonPrimary.vue';
import InformationIcon from '@/components/shared/Icons/InformationIcon.vue';
import api from '@/utils/api';

export default {
	name: 'InvestorForm',
	directives: { mask },
	components: { GenericMultiselect, SpinnerIcon, DeleteModal, ButtonPrimary, InformationIcon },
	props: {
		isWeb: {
			required: true,
		},
	},
	data: () => ({
		investor: {
			socialReason: '',
			fantasyName: '',
			cnpj: '',
			hasNDA: '',
		},
		requiredFields: {
			cnpj: false,
		},
		newInvestor: false,
		isEditing: false,
		showDeleteModal: false,
		showCompanyInput: true,
		debounce: null,
		cnpjErrorMessage: '',
		http: api.getInstance().http,
		isLoadingCnpj: false,
		isCnpjValid: false,
	}),
	created() {},
	mounted() {
		if (this.$route.query.id) {
			this.getInvestor({ vm: this, id: this.$route.query.id }).then((res) => {
				const selectedInvestor = res.data;
				const status = selectedInvestor.hasNDA ? this.statusOptions[0] : this.statusOptions[1];

				this.investor.socialReason = selectedInvestor.socialReason;
				this.investor.fantasyName = selectedInvestor.fantasyName;
				this.investor.cnpj = selectedInvestor.cnpj;
				this.investor.hasNDA = status.id;
				this.$refs.status.setValue(status);
			});
		} else {
			this.newInvestor = true;
			this.investor.hasNDA = true;
			this.$refs.status.setValue(this.statusOptions[0]);
		}
	},
	methods: {
		...mapActions('investorManagement', {
			getInvestor: GET_INVESTOR_BY_ID,
			createNewInvestor: CREATE_INVESTOR,
			updateInvestor: UPDATE_INVESTOR,
			deleteInvestor: DELETE_INVESTOR,
		}),
		changeStatusInput(param) {
			this.investor.hasNDA = param.value.id;
		},
		handleDeleteModal() {
			this.showDeleteModal = !this.showDeleteModal;
		},
		confirmDelete() {
			this.deleteInvestor({ vm: this, id: this.$route.query.id })
				.then((res) => {
					this.handleDeleteModal();
					if (res.status == 200) {
						this.$router.push({ path: '/dashboard/investors' });
					}
				})
				.catch((e) => {
					this.handleDeleteModal();
				});
		},
		getInvestorData() {
			return {
				cnpj: this.investor.cnpj,
				hasNDA: this.investor.hasNDA,
			};
		},
		createInvestor() {
			const body = this.getInvestorData();
			this.createNewInvestor({ vm: this, body }).then((res) => {
				if (res.status == 201) {
					this.$router.push({ path: '/dashboard/investors' });
				}
			});
		},
		confirmUpdate() {
			const body = this.getInvestorData();
			this.updateInvestor({ vm: this, id: this.$route.query.id, body }).then((res) => {
				if (res.status == 200) {
					this.isEditing = false;
				}
			});
		},
		validateFields() {
			this.requiredFields = {
				cnpj: true,
			};

			if (this.investor.cnpj) this.requiredFields.cnpj = false;

			if (!this.investor.cnpj) {
				this.cnpjErrorMessage = 'Campo obrigatório.';
				return this.$toastr.e('CNPJ inválido.');
			}

			if (!this.newInvestor) {
				this.confirmUpdate();
			} else {
				this.createInvestor();
			}
		},
		setLoadingCnpj(value) {
			this.isLoadingCnpj = value;
		},
		searchByCompanyCnpj() {
			this.requiredFields.cnpj = false;
			if (this.sanitizedCnpj.length === 14) {
				this.setLoadingCnpj(true);
				clearTimeout(this.debounce);
				this.debounce = setTimeout(() => {
					this.http
						.get(`cnpj/${this.sanitizedCnpj}`)
						.then((res) => {
							this.investor.socialReason = res.data.social_reason;
							this.investor.fantasyName = res.data.fantasy_name;
							this.isCnpjValid = true;
						})
						.catch((error) => {
							this.investor.fantasyName = '';
							this.investor.socialReason = '';
							this.isCnpjValid = false;
							switch (error.response.status) {
								case 400:
									this.requiredFields.cnpj = true;
									this.cnpjErrorMessage = 'CNPJ inválido.';
									break;
								case 404:
									this.requiredFields.cnpj = true;
									this.cnpjErrorMessage = 'CNPJ não encontrado.';
									break;
								default:
									this.requiredFields.cnpj = false;
									this.cnpjErrorMessage = 'Campo obrigatório.';
									this.$toastr.e(error.response.data.message);
							}
						})
						.finally(() => {
							this.setLoadingCnpj(false);
						});
				}, 600);
			} else {
				this.isCnpjValid = false;
			}
		},
	},
	computed: {
		...mapGetters('investorManagement', ['isLoadingInvestors']),
		isSafari() {
			return navigator.vendor == 'Apple Computer, Inc.';
		},
		statusOptions() {
			return [
				{
					id: true,
					name: 'Aprovado',
				},
				{
					id: false,
					name: 'Pendente',
				},
			];
		},
		sanitizedCnpj() {
			return this.investor.cnpj.replace(/\D/g, '');
		},
	},
};
</script>

<style lang="scss" scoped>
@import 'InvestorForm.scss';
</style>
